import React, { useState } from 'react';
import { ArrowRight, MapPin, CalendarBlank, Phone } from '@phosphor-icons/react';
import { motion } from '@/lib/motion';
import { BookingWidget } from './BookingWidget';
import { InquiryModal } from './InquiryModal';
import { getAIImage } from '../data/mock';

export const ContactSection = ({ showPartners = true }) => {
  const [showBooking, setShowBooking] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);

  return (
    <>
    <section id="contact" className="py-12 md:py-16 bg-gallery" data-testid="contact-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <span className="inline-flex items-center gap-2 text-bronze text-[9px] md:text-[10px] font-body font-semibold uppercase tracking-[0.2em] mb-2">
            <span className="w-5 h-px bg-bronze" />
            DISCOVER
            <span className="w-5 h-px bg-bronze" />
          </span>
          <h2 className="font-display text-xl md:text-2xl font-bold text-obsidian uppercase tracking-tight">
            VISIT OUR EXPERIENCE CENTRE
          </h2>
          <p className="font-body text-[13px] md:text-[14px] text-concrete mt-2 max-w-md mx-auto">
            Explore our material library and discuss your project with our design team.
          </p>
        </div>

        {/* Image Grid + Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Large Image */}
          <div className="lg:col-span-2 aspect-[16/10] md:aspect-[16/9] overflow-hidden">
            <img loading="lazy" decoding="async" 
              src={getAIImage('contact_showroom')}
              alt="IHGIND Experience Centre"
              width={1280}
              height={800}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Info Card */}
          <div className="bg-obsidian text-white p-5 flex flex-col justify-between">
            <div>
              <h3 className="font-display text-[11px] md:text-sm font-bold uppercase tracking-[0.12em] text-bronze mb-3">
                EXPERIENCE CENTRE
              </h3>
              <div className="flex items-start gap-2 mb-3">
                <MapPin weight="bold" className="w-3.5 h-3.5 text-bronze flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-body text-[12px] text-white/80 leading-relaxed">
                    JEBEL ALI INDUSTRIAL AREA 1<br />
                    DUBAI, UAE
                  </p>
                </div>
              </div>
              <a 
                href="https://maps.google.com/?q=Jebel+Ali+Industrial+Area+1+Dubai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.12em] text-bronze hover:text-white transition-colors"
              >
                VIEW ON MAP
                <ArrowRight weight="bold" className="w-3 h-3" />
              </a>
            </div>

            <div className="space-y-2 mt-4">
              <motion.button
                onClick={() => setShowBooking(true)}
                className="w-full py-2.5 bg-bronze text-obsidian font-body text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                data-testid="book-visit-btn"
              >
                <CalendarBlank weight="bold" className="w-3.5 h-3.5" />
                BOOK A VISIT
              </motion.button>
              <a 
                href="tel:+97142585754"
                className="w-full py-2.5 border border-white/20 text-white font-body text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-white hover:text-obsidian transition-colors flex items-center justify-center gap-2"
              >
                <Phone weight="bold" className="w-3.5 h-3.5" />
                +971 4 258 5754
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Row - Small Images + CTA */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="aspect-[4/3] overflow-hidden">
            <img loading="lazy" decoding="async" 
              src={getAIImage('contact_villa')}
              alt="Material Library"
              width={800}
              height={600}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          <div className="aspect-[4/3] overflow-hidden">
            <img loading="lazy" decoding="async" 
              src={getAIImage('finishes_hero')}
              alt="Finish Samples"
              width={800}
              height={600}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          <div className="aspect-[4/3] overflow-hidden hidden md:block">
            <img loading="lazy" decoding="async" 
              src={getAIImage('contact_polishing')}
              alt="Workshop Tour"
              width={800}
              height={600}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          {/* CTA Card */}
          <div className="aspect-[4/3] bg-obsidian flex flex-col items-center justify-center p-4 text-center col-span-2 md:col-span-1">
            <h4 className="font-display text-[10px] md:text-xs font-bold text-white uppercase tracking-[0.12em] mb-2">
              START YOUR PROJECT
            </h4>
            <motion.button
              onClick={() => setShowInquiry(true)}
              className="px-4 py-2 bg-bronze text-obsidian font-body text-[9px] md:text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors"
              whileHover={{ scale: 1.02 }}
              data-testid="start-project-btn"
            >
              GET IN TOUCH
            </motion.button>
          </div>
        </div>
      </div>

      <BookingWidget isOpen={showBooking} onClose={() => setShowBooking(false)} />
      <InquiryModal isOpen={showInquiry} onClose={() => setShowInquiry(false)} />
    </section>

    {/* Who We Collaborate With */}
    {showPartners && (
    <section className="py-12 md:py-16 bg-gallery" data-testid="collaborate-section">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-bronze text-[9px] md:text-[10px] font-body font-semibold uppercase tracking-[0.2em] mb-2">
            <span className="w-5 h-px bg-bronze" />
            PARTNERSHIPS
            <span className="w-5 h-px bg-bronze" />
          </span>
          <h2 className="font-display text-xl md:text-2xl font-bold text-obsidian uppercase tracking-tight">
            WHO WE COLLABORATE WITH
          </h2>
          <p className="font-body text-[13px] text-concrete mt-2 max-w-md mx-auto">
            Trusted by leading architects, developers, and brands across the Middle East and Africa.
          </p>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-6 items-center opacity-60">
          {['Emaar', 'ALDAR', 'Nakheel', 'Meraas', 'DAMAC', 'Dubai Holding',
            'Dar Al Arkan', 'Select Group', 'Omniyat', 'Eagle Hills', 'Azizi', 'Sobha'].map((name) => (
            <div key={name} className="flex items-center justify-center h-16 bg-white rounded border border-neutral-200 px-4">
              <span className="font-display text-[10px] md:text-xs font-bold text-obsidian/70 uppercase tracking-wider text-center">{name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
    )}
    </>
  );
};

export default ContactSection;
